/* Markdown backup: records <-> a folder of .md files.
 *
 * Layout, deliberately Obsidian-shaped so the backup is useful on its own:
 *
 *   _longhand.md                        manifest (version, export date, counts)
 *   The Salt Meridian/
 *     _space.md                         space metadata; description is the body
 *     01 Chapter One — The Keeper.md    synopsis in frontmatter, prose in the body
 *     notes/
 *       Maren Voss.md
 *
 * Frontmatter carries everything markdown has nowhere to put — id, synopsis,
 * order, timestamps — because without it a restore silently loses them.
 */

import { KINDS } from './store.js';

export const MANIFEST = '_longhand.md';
export const SPACE_FILE = '_space.md';
const NOTES_DIR = 'notes';

const now = () => new Date().toISOString();
const uid = () => (globalThis.crypto?.randomUUID
  ? crypto.randomUUID()
  : 'id-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10));

/* --------------------------------------------------------- frontmatter */

const BARE_OK = /^[A-Za-z0-9][^:#\n"']*$/;
const RESERVED = /^(true|false|null|yes|no|on|off|~)$/i;

function emitScalar(v) {
  if (typeof v === 'number') return String(v);
  const s = String(v ?? '');
  if (s === '') return '""';
  if (BARE_OK.test(s) && !RESERVED.test(s) && s === s.trim()) return s;
  return JSON.stringify(s);
}

function parseScalar(s) {
  const t = s.trim();
  if (t.startsWith('"')) { try { return JSON.parse(t); } catch (e) { return t.slice(1, -1); } }
  return t;
}

export function stringifyDoc(fields, body) {
  const lines = ['---'];
  for (const [k, v] of Object.entries(fields)) {
    if (v === undefined || v === null) continue;
    lines.push(k + ': ' + emitScalar(v));
  }
  lines.push('---', '');
  return lines.join('\n') + (body || '') + '\n';
}

export function parseDoc(text) {
  const src = String(text).replace(/^﻿/, '').replace(/\r\n/g, '\n');
  if (!src.startsWith('---\n')) return { fields: {}, body: src.trim() };
  const end = src.indexOf('\n---', 3);
  if (end === -1) return { fields: {}, body: src.trim() };

  const fields = {};
  for (const line of src.slice(4, end).split('\n')) {
    const i = line.indexOf(':');
    if (i <= 0) continue;
    fields[line.slice(0, i).trim()] = parseScalar(line.slice(i + 1));
  }
  const rest = src.slice(end + 4).replace(/^\n+/, '');
  return { fields, body: rest.replace(/\s+$/, '') };
}

/* ------------------------------------------------------------ filenames */

export function safeName(name, fallback = 'Untitled') {
  const s = String(name || '')
    .replace(/[\/\\:*?"<>|]/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/^[.\s]+|[.\s]+$/g, '')
    .slice(0, 120);
  return s || fallback;
}

function uniquely(used, base, ext = '') {
  let name = base + ext;
  let n = 2;
  while (used.has(name.toLowerCase())) name = base + ' (' + n++ + ')' + ext;
  used.add(name.toLowerCase());
  return name;
}

/* --------------------------------------------------------------- export */

/**
 * @param {{spaces:[],documents:[],notes:[]}} records
 * @param {{spaceId?: string}} opts  limit to a single space
 * @returns {{path: string, text: string, date: Date}[]}
 */
export function toFiles(records, { spaceId = null } = {}) {
  const live = k => (records[k] || []).filter(r => !r.deletedAt)
    .sort((a, b) => a.position - b.position || String(a.id).localeCompare(String(b.id)));

  const spaces = live('spaces').filter(s => !spaceId || s.id === spaceId);
  const keep = new Set(spaces.map(s => s.id));
  const docs = live('documents').filter(d => keep.has(d.spaceId));
  const notes = live('notes').filter(n => keep.has(n.spaceId));

  const files = [];
  const usedFolders = new Set();
  const stamp = new Date();

  files.push({
    path: MANIFEST,
    date: stamp,
    text: stringifyDoc(
      { longhand: 1, exported: stamp.toISOString(), spaces: spaces.length, documents: docs.length, notes: notes.length },
      ['# Longhand backup', '',
       'One folder per space. `_space.md` holds the space itself; numbered files are',
       'documents in order; `notes/` holds characters and world notes. Frontmatter',
       'carries ids, synopses and timestamps — keep it and the restore is lossless.'].join('\n')
    )
  });

  for (const sp of spaces) {
    const folder = uniquely(usedFolders, safeName(sp.name, 'Space'));
    files.push({
      path: folder + '/' + SPACE_FILE,
      date: new Date(sp.updatedAt || stamp),
      text: stringifyDoc(
        { id: sp.id, name: sp.name, kind: sp.kind, goal: sp.goal, position: sp.position, updated: sp.updatedAt },
        sp.description || ''
      )
    });

    const spaceDocs = docs.filter(d => d.spaceId === sp.id);
    const pad = Math.max(2, String(spaceDocs.length).length);
    const usedDocs = new Set();
    spaceDocs.forEach((d, i) => {
      const prefix = String(i + 1).padStart(pad, '0') + ' ';
      const name = uniquely(usedDocs, prefix + safeName(d.title, 'Untitled'), '.md');
      files.push({
        path: folder + '/' + name,
        date: new Date(d.updatedAt || stamp),
        text: stringifyDoc(
          { id: d.id, title: d.title, synopsis: d.synopsis, position: d.position, updated: d.updatedAt },
          d.content || ''
        )
      });
    });

    const usedNotes = new Set();
    for (const n of notes.filter(x => x.spaceId === sp.id)) {
      const name = uniquely(usedNotes, safeName(n.title, 'Note'), '.md');
      files.push({
        path: folder + '/' + NOTES_DIR + '/' + name,
        date: new Date(n.updatedAt || stamp),
        text: stringifyDoc(
          { id: n.id, kind: n.kind, title: n.title, position: n.position, updated: n.updatedAt },
          n.body || ''
        )
      });
    }
  }

  return files;
}

/* --------------------------------------------------------------- import */

function normalise(files) {
  const cleaned = files
    .map(f => ({ ...f, path: String(f.path).replace(/\\/g, '/').replace(/^\.\//, '').replace(/^\/+/, '') }))
    .filter(f => f.path.endsWith('.md')
      && !f.path.split('/').some(seg => seg.startsWith('__MACOSX') || seg === '.DS_Store'));

  // Strip a shared wrapper directory (folder pickers add one). Never strip the
  // last directory level — with a single space that folder *is* the space.
  const segs = cleaned.map(f => f.path.split('/'));
  while (segs.every(s => s.length > 2 && s[0] === segs[0][0])) {
    segs.forEach(s => s.shift());
  }
  return cleaned.map((f, i) => ({ ...f, path: segs[i].join('/') }));
}

/**
 * @param {{path: string, text: string}[]} input
 * @returns {{records: object, report: object}}
 */
export function fromFiles(input) {
  const files = normalise(input);
  if (!files.length) throw new Error('No .md files found in that backup.');

  const records = { spaces: [], documents: [], notes: [] };
  const warnings = [];
  const stamp = now();

  // A space is any folder holding a _space.md; failing that, any top-level folder.
  const roots = new Set();
  for (const f of files) {
    const parts = f.path.split('/');
    if (parts.length > 1 && parts[parts.length - 1] === SPACE_FILE) roots.add(parts.slice(0, -1).join('/'));
  }
  if (!roots.size) {
    for (const f of files) {
      const parts = f.path.split('/');
      if (parts.length > 1) roots.add(parts[0]);
    }
    if (roots.size) warnings.push('No _space.md found — treated each top-level folder as a space.');
  }
  if (!roots.size) throw new Error('No space folders found in that backup.');

  for (const root of [...roots].sort()) {
    const inSpace = files.filter(f => f.path.startsWith(root + '/'));
    const spaceFile = inSpace.find(f => f.path === root + '/' + SPACE_FILE);
    const meta = spaceFile ? parseDoc(spaceFile.text) : { fields: {}, body: '' };
    const spaceId = meta.fields.id || uid();

    records.spaces.push({
      id: spaceId,
      name: meta.fields.name || root.split('/').pop(),
      kind: meta.fields.kind || '',
      description: meta.body || '',
      goal: Number(meta.fields.goal) || 0,
      position: meta.fields.position !== undefined ? Number(meta.fields.position) : records.spaces.length,
      updatedAt: meta.fields.updated || stamp,
      deletedAt: null
    });

    const rel = f => f.path.slice(root.length + 1);
    const docFiles = inSpace
      .filter(f => { const r = rel(f); return r && !r.includes('/') && r !== SPACE_FILE; })
      .sort((a, b) => a.path.localeCompare(b.path));
    const noteFiles = inSpace
      .filter(f => rel(f).startsWith(NOTES_DIR + '/'))
      .sort((a, b) => a.path.localeCompare(b.path));

    docFiles.forEach((f, i) => {
      const { fields, body } = parseDoc(f.text);
      records.documents.push({
        id: fields.id || uid(), spaceId,
        title: fields.title || rel(f).replace(/\.md$/, '').replace(/^\d+[ .-]+/, ''),
        synopsis: fields.synopsis || '',
        content: body,
        position: fields.position !== undefined ? Number(fields.position) : i,
        updatedAt: fields.updated || stamp, deletedAt: null
      });
    });

    noteFiles.forEach((f, i) => {
      const { fields, body } = parseDoc(f.text);
      records.notes.push({
        id: fields.id || uid(), spaceId,
        kind: fields.kind === 'character' ? 'character' : 'world',
        title: fields.title || rel(f).split('/').pop().replace(/\.md$/, ''),
        body,
        position: fields.position !== undefined ? Number(fields.position) : i,
        updatedAt: fields.updated || stamp, deletedAt: null
      });
    });
  }

  return {
    records,
    report: {
      spaces: records.spaces.length,
      documents: records.documents.length,
      notes: records.notes.length,
      warnings
    }
  };
}

/** Total record count, for reporting. */
export const countRecords = r => KINDS.reduce((a, k) => a + (r[k] || []).length, 0);
